import React, { useRef, useEffect } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { motion } from 'motion/react';
import { Step } from '../../../state/map';
import { stepConfigs } from '../../../config/steps';

interface StepperProps {
  activeStep: Step;
  onStepClick: (step: Step) => void;
}

const Stepper: React.FC<StepperProps> = ({ activeStep, onStepClick }) => {
  const theme = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to center the active step
  useEffect(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const activeIndex = stepConfigs.findIndex(config => config.id === activeStep);
      
      if (activeIndex !== -1) {
        const stepWidth = 120; // Approximate width of each step pill
        const containerWidth = container.clientWidth;
        const scrollPosition = (activeIndex * stepWidth) - (containerWidth / 2) + (stepWidth / 2);
        
        container.scrollTo({
          left: Math.max(0, scrollPosition),
          behavior: 'smooth'
        });
      }
    }
  }, [activeStep]);

  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        display: 'flex',
        gap: 1,
        overflowX: 'auto',
        overflowY: 'hidden',
        paddingX: 2,
        paddingY: 1,
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        // Smooth scrolling
        scrollBehavior: 'smooth',
        // Add momentum scrolling on iOS
        WebkitOverflowScrolling: 'touch'
      }}
    >
      {stepConfigs.map((config, index) => {
        return (
          <Box
            key={config.id}
            component={motion.div}
            whileTap={{ scale: 0.95 }}
            onClick={() => onStepClick(config.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 'fit-content',
              cursor: 'pointer',
              padding: '8px 16px',
              borderRadius: '20px',
              backgroundColor: 'transparent',
              border: '2px solid',
              borderColor: theme.palette.divider,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                borderColor: theme.palette.primary.main,
                transform: 'translateY(-1px)',
                boxShadow: theme.shadows[2]
              }
            }}
          >
            {/* Step Number */}
            <Box
              sx={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.secondary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 600,
                marginRight: '8px',
                flexShrink: 0
              }}
            >
              {index + 1}
            </Box>

            {/* Step Label */}
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 500,
                fontSize: '14px',
                whiteSpace: 'nowrap'
              }}
            >
              {config.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default Stepper;
